import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { API_ENDPOINTS } from '../config';
import { useAuth } from '../contexts/AuthContext';
import { prettyStockAlgoLabel } from '../utils/vectorBasketDisplay';
import './AdminFeedback.css';
import './AdminSheetManager.css';
import './AdminEquityStrategies.css';
import './AdminScenarioData.css';
import { getBearerAuthHeaders } from '../utils/authFetchHeaders';
import { getEntityDisplayName, getResultsNavLabel, isVectorEntity } from '../utils/activeEntity';
import { IconCopy } from '../components/Icons';
import StrategyNameEditor from '../components/admin/StrategyNameEditor';

const INSTRUMENT_EDITOR_TYPES = new Set(['trade-journal', 'equity', 'futures']);
const INSTRUMENT_CLASSES = [
  { value: 'futures', label: 'Futures' },
  { value: 'crypto', label: 'Crypto' },
  { value: 'stock', label: 'Stock / ETF' },
];

/** localStorage key: last strategy selected in the Strategy Data editor. */
const SCENARIO_DATA_SELECTED_KEY = 'adminScenarioData.selectedKey';

const PAGE_SIZE = 75;
const DEFAULT_BASE = 100000;

const MASTER_WEEKDAYS = [
  { key: 'monday', short: 'Mon' },
  { key: 'tuesday', short: 'Tue' },
  { key: 'wednesday', short: 'Wed' },
  { key: 'thursday', short: 'Thu' },
  { key: 'friday', short: 'Fri' },
];

const PORTFOLIO_LABELS = {
  portfolio_1x: 'Equity Portfolio 1×',
  portfolio_2_25x: 'Equity Portfolio 2.25×',
};

// Delegates to the shared helper so the active-entity header (X-RiskLo-Entity)
// rides along. Building the Authorization header locally silently dropped it,
// which made every admin screen query Vector no matter which brand was selected.

/** First / Previous / page box / Next / Last — for the long Strategy Data tables. */
function TablePager({ page, pageCount, onPage }) {
  const [draft, setDraft] = React.useState(String(page + 1));
  React.useEffect(() => { setDraft(String(page + 1)); }, [page]);
  if (pageCount <= 1) return null;
  const last = pageCount - 1;
  const go = (n) => onPage(Math.min(last, Math.max(0, n)));
  const jump = () => {
    const n = Number.parseInt(draft, 10);
    if (Number.isFinite(n)) go(n - 1); else setDraft(String(page + 1));
  };
  return (
    <div className="admin-scenario-pagination">
      <button type="button" className="admin-sheet-btn" disabled={page <= 0} onClick={() => go(0)} title="First page">
        « First
      </button>
      <button type="button" className="admin-sheet-btn" disabled={page <= 0} onClick={() => go(page - 1)}>
        Previous
      </button>
      <span className="admin-scenario-page-jump">
        Page
        <input
          type="number"
          min={1}
          max={pageCount}
          value={draft}
          aria-label="Page number"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={jump}
          onKeyDown={(e) => { if (e.key === 'Enter') jump(); }}
        />
        of {pageCount}
      </span>
      <button type="button" className="admin-sheet-btn" disabled={page >= last} onClick={() => go(page + 1)}>
        Next
      </button>
      <button type="button" className="admin-sheet-btn" disabled={page >= last} onClick={() => go(last)} title="Last page">
        Last »
      </button>
    </div>
  );
}

function authHeaders() {
  return { 'Content-Type': 'application/json', ...getBearerAuthHeaders() };
}

function downloadTextAsFile(text, filename) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  URL.revokeObjectURL(url);
}

function tradeImportFormatLabel(sourceFormat) {
  if (!sourceFormat) return 'CSV';
  return {
    'tradesviz-trades': 'TradesViz trades export',
    'tradesviz-executions': 'TradesViz executions export',
    'ninjatrader-executions': 'NinjaTrader executions export',
    'tradingview-trades': 'TradingView / VECTOR trades list',
    normalized: 'normalized trade-list',
    journal: 'journal export',
  }[sourceFormat] || sourceFormat;
}

function tradeImportFingerprint(trade) {
  const round2 = (n) => (n == null || !Number.isFinite(Number(n)) ? '' : Number(n).toFixed(2));
  return [
    trade.trade_date,
    String(trade.symbol || '').trim().toLowerCase(),
    String(trade.position || '').toLowerCase(),
    round2(trade.net_pnl),
    String(trade.volume ?? ''),
    round2(trade.entry_price),
    round2(trade.exit_price),
  ].join('|');
}

/** Append parsed trades to the editor; skip rows that match an existing trade. */
function mergePartialTrades(existing, incoming) {
  const seen = new Set(existing.map(tradeImportFingerprint));
  const added = [];
  incoming.forEach((t) => {
    const fp = tradeImportFingerprint(t);
    if (seen.has(fp)) return;
    seen.add(fp);
    added.push({ ...t, source: t.source || 'csv' });
  });
  const merged = [...existing, ...added].sort((a, b) => {
    if (a.trade_date !== b.trade_date) return a.trade_date.localeCompare(b.trade_date);
    return (a.sort_index || 0) - (b.sort_index || 0);
  });
  const byDate = new Map();
  return {
    trades: merged.map((t) => {
      const count = byDate.get(t.trade_date) || 0;
      byDate.set(t.trade_date, count + 1);
      return { ...t, sort_index: count };
    }),
    addedCount: added.length,
    skippedDupes: incoming.length - added.length,
  };
}

/** Live NT8 / webhook rows are merged at read time — never persist them on save. */
function isLiveOverlayTradeSource(source) {
  const s = String(source || '').trim().toLowerCase();
  return s === 'nt8_webhook' || s === 'webhook';
}

/** Rows a live feed writes and rebuilds (NT8, TradingView alerts, trade fills). */
function isFeedTradeSource(source) {
  return isLiveOverlayTradeSource(source) || String(source || '').trim().toLowerCase() === 'webhook_fills';
}

function journalTradesForSave(trades) {
  return (trades || []).filter((t) => !isLiveOverlayTradeSource(t.source));
}

/**
 * Live NT8 webhook rows are merged in at read time, so removing or editing one
 * only sticks if the server is told which webhook trade it was:
 *  - removed: its trade id is excluded from the merge from now on;
 *  - edited: the row is saved as your own (source "manual") and the original
 *    webhook trade is excluded. Your own row then owns that day, which would
 *    silently drop the day's other webhook trades, so they are saved as your
 *    own rows too.
 */
function planJournalSave(currentTrades, loadedTrades, { keepLiveOnOwnedDays = false } = {}) {
  const loadedWebhookIds = new Set(
    (loadedTrades || [])
      .filter((t) => isLiveOverlayTradeSource(t.source) && Number(t.nt8_trade_id) > 0)
      .map((t) => Number(t.nt8_trade_id)),
  );
  const current = currentTrades || [];
  const editedDates = new Set(
    current
      .filter((t) => !isLiveOverlayTradeSource(t.source) && Number(t.nt8_trade_id) > 0)
      .map((t) => t.trade_date),
  );
  // A day with your own row is owned by the journal, and the live merge then
  // skips that day's NT8 trades. When asked, keep them as your own rows too.
  if (keepLiveOnOwnedDays) {
    liveConflictDays(current).forEach((d) => editedDates.add(d));
  }
  const rows = current.map((t) => (
    isLiveOverlayTradeSource(t.source) && editedDates.has(t.trade_date)
      ? { ...t, source: 'manual' }
      : t
  ));
  const stillLive = new Set(
    rows.filter((t) => isLiveOverlayTradeSource(t.source) && Number(t.nt8_trade_id) > 0)
      .map((t) => Number(t.nt8_trade_id)),
  );
  return {
    trades: journalTradesForSave(rows),
    removedNt8TradeIds: [...loadedWebhookIds].filter((id) => !stillLive.has(id)),
  };
}

/** Days that have both your own rows and live NT8 rows (the live ones are dropped on save). */
function liveConflictDays(trades) {
  const own = new Set();
  const live = new Set();
  (trades || []).forEach((t) => {
    if (isLiveOverlayTradeSource(t.source)) live.add(t.trade_date);
    else own.add(t.trade_date);
  });
  return [...own].filter((d) => live.has(d)).sort();
}

/** Saved rows that aren't in the reloaded table (same date, symbol and P&L). */
function missingAfterSave(saved, reloaded) {
  const key = (t) => [String(t.trade_date).slice(0, 10), String(t.symbol || '').trim().toLowerCase(),
    Number(t.net_pnl || 0).toFixed(2)].join('|');
  const have = new Map();
  (reloaded || []).forEach((t) => have.set(key(t), (have.get(key(t)) || 0) + 1));
  const missing = [];
  (saved || []).forEach((t) => {
    const k = key(t);
    const n = have.get(k) || 0;
    if (n > 0) have.set(k, n - 1);
    else missing.push(t);
  });
  return missing;
}

function confirmFullTradeImport(filename) {
  const label = filename ? `"${filename}"` : 'the selected file';
  return window.confirm(
    `Replace ALL trades with ${label}?\n\n`
    + 'This import replaces every trade currently shown for this strategy and rebuilds the equity curve immediately. '
    + 'Existing journal data will be overwritten — this is not undoable from this screen.\n\n'
    + 'To append trades without replacing what is already here, use "Add partial data" instead.\n\n'
    + 'Continue with full replace?',
  );
}

function PartialDataFileInput({ disabled, onSelect }) {
  return (
    <label className="admin-equity-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
      <span>Add partial data</span>
      <input
        type="file"
        accept=".csv,text/csv"
        disabled={disabled}
        onChange={onSelect}
      />
    </label>
  );
}

function parseAttachmentFilename(contentDisposition, fallback) {
  if (!contentDisposition) return fallback;
  const match = contentDisposition.match(/filename="?([^"]+)"?/i);
  return match?.[1] || fallback;
}

function fmtPct(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : '';
  return `${sign}${value.toFixed(2)}%`;
}

function fmtMoney(value) {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const sign = value > 0 ? '+' : value < 0 ? '-' : '';
  return `${sign}$${Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/** Friendly label for Vector Capital 1H stock algos in dropdowns. */
function formatScenarioDataOptionLabel(o) {
  const base = prettyStockAlgoLabel(o.label || o.displayName || o.key);
  if (o.type === 'trade-journal') {
    return `${base}${o.tradeCount != null ? ` (${o.tradeCount} trades)` : ' (trades)'}`;
  }
  if (o.pointCount != null) {
    return `${base} (${o.pointCount} days)`;
  }
  return base;
}
const SOURCE_LABELS = {
  csv: 'CSV upload',
  manual: 'Manual',
  webhook: 'Webhook',
  nt8_webhook: 'NT8 Webhook',
  'kairos-webhook': 'Webhook (Kairos)',
  nt8_addon: 'NT8 add-on',
  sheet: 'Results Spreadsheet',
  mixed: 'Mixed',
  baseline: 'Baseline start',
  'nt-connector': 'NT Connector',
  'nt-connector-start': 'NT Connector',
  'og-snapshot': 'OG snapshot',
  'trade-journal-csv': 'CSV upload',
};
function sourceLabel(source) {
  const s = String(source || '').trim().toLowerCase();
  return SOURCE_LABELS[s] || 'Unknown';
}

function deriveTradeRows(points, defaultSource = null) {
  const fallback = defaultSource ? String(defaultSource).trim().toLowerCase() : null;
  return (points || []).map((p, i, arr) => {
    const prev = i > 0 ? Number(arr[i - 1]?.equity) : null;
    const cur = Number(p.equity);
    const dailyPnl = prev !== null && Number.isFinite(prev) && Number.isFinite(cur) ? cur - prev : null;
    const dailyReturnPct = prev !== null && Number.isFinite(prev) && prev !== 0 && Number.isFinite(cur)
      ? ((cur / prev) - 1) * 100
      : null;
    return {
      date: p.date,
      equity: cur,
      dailyPnl,
      dailyReturnPct,
      source: p.source || fallback || null,
      // 'live' | 'backtest' from the CSV's segment column or the webhook's flag.
      segment: p.segment || null,
    };
  });
}

function calcRoiFromPnl(netPnl, baseCapital = DEFAULT_BASE) {
  if (!Number.isFinite(netPnl) || !baseCapital) return null;
  return Math.round((netPnl / baseCapital) * 10000) / 100;
}

function applyPnlDerivedFields(trade, baseCapital = DEFAULT_BASE) {
  const net_pnl = Number(trade.net_pnl);
  if (!Number.isFinite(net_pnl)) return trade;
  return {
    ...trade,
    net_pnl,
    net_roi: calcRoiFromPnl(net_pnl, baseCapital),
    status: net_pnl >= 0 ? 'Win' : 'Loss',
  };
}

function applyRoiDerivedFields(trade, baseCapital = DEFAULT_BASE) {
  const net_roi = Number(trade.net_roi);
  if (!Number.isFinite(net_roi)) return trade;
  const net_pnl = Math.round((baseCapital * net_roi / 100) * 100) / 100;
  return {
    ...trade,
    net_roi,
    net_pnl,
    status: net_pnl >= 0 ? 'Win' : 'Loss',
  };
}

const FUTURES_TRADE_SORT_COLUMNS = [
  { key: 'trade_date', label: 'Trade Date', type: 'date' },
  { key: 'symbol', label: 'Symbol', type: 'string' },
  { key: 'position', label: 'Side', type: 'string' },
  { key: 'status', label: 'Status', type: 'string' },
  { key: 'volume', label: 'Volume', type: 'number' },
  { key: 'entry_price', label: 'Entry Price', type: 'number' },
  { key: 'exit_price', label: 'Exit Price', type: 'number' },
  { key: 'net_pnl', label: 'Net P&L', type: 'number' },
  { key: 'net_roi', label: 'Net ROI', type: 'number' },
  { key: 'segment', label: 'Live / Backtest', type: 'string' },
  { key: 'source', label: 'Source', type: 'string' },
];

const VECTOR_EQUITY_TRADE_SORT_COLUMNS = [
  { key: 'trade_date', label: 'Exit Date', type: 'date' },
  { key: 'symbol', label: 'Symbol', type: 'string' },
  { key: 'position', label: 'Side', type: 'string' },
  { key: 'status', label: 'Status', type: 'string' },
  { key: 'volume', label: 'Volume', type: 'number' },
  { key: 'entry_price', label: 'Entry Price', type: 'number' },
  { key: 'exit_price', label: 'Exit Price', type: 'number' },
  { key: 'net_pnl', label: 'Net P&L ($)', type: 'number' },
  { key: 'net_roi', label: 'Net P&L (%)', type: 'number' },
  { key: 'segment', label: 'Live / Backtest', type: 'string' },
  { key: 'source', label: 'Source', type: 'string' },
];

const EQUITY_POINT_SORT_COLUMNS = [
  { key: 'date', label: 'Date', type: 'date' },
  { key: 'equity', label: 'Equity ($)', type: 'number' },
  { key: 'dailyPnl', label: 'Daily P&L', type: 'number' },
  { key: 'dailyReturnPct', label: 'Daily Return', type: 'number' },
  { key: 'segment', label: 'Live / Backtest', type: 'string' },
  { key: 'source', label: 'Source', type: 'string' },
];

function tickerFromVectorStrategyName(name) {
  const raw = String(name || '').trim();
  if (/Vector Capital — TSLA - PYTH \(1H Algo\)/i.test(raw)) return 'TSLA';
  const m = raw.match(/Vector Capital — ([A-Za-z0-9]+) \(1H Algo\)/i);
  return m ? m[1].toUpperCase() : '';
}

function compareTradeField(a, b, key, type) {
  const left = a?.[key];
  const right = b?.[key];
  if (type === 'number') {
    const ln = Number(left);
    const rn = Number(right);
    const l = Number.isFinite(ln) ? ln : -Infinity;
    const r = Number.isFinite(rn) ? rn : -Infinity;
    return l - r;
  }
  if (type === 'date') {
    return String(left || '').localeCompare(String(right || ''));
  }
  return String(left || '').localeCompare(String(right || ''), undefined, { sensitivity: 'base' });
}

function TradeJournalEditor({
  trades,
  onChange,
  baseCapital = DEFAULT_BASE,
  variant = 'futures',
  defaultSymbol = '',
  onAddRejected = null,
}) {
  const isVectorEquity = variant === 'vector-equity';
  const sortColumns = isVectorEquity ? VECTOR_EQUITY_TRADE_SORT_COLUMNS : FUTURES_TRADE_SORT_COLUMNS;
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [sortKey, setSortKey] = useState('trade_date');
  const [sortDir, setSortDir] = useState('desc');
  const [newTrade, setNewTrade] = useState({
    trade_date: '',
    symbol: '',
    position: '',
    status: 'Win',
    volume: '',
    entry_price: '',
    exit_price: '',
    net_pnl: '',
    net_roi: '',
  });

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const indexed = trades.map((trade, index) => ({ trade, index }));
    const matched = !q
      ? indexed
      : indexed.filter(({ trade }) =>
        trade.trade_date.includes(q)
        || String(trade.symbol || '').toLowerCase().includes(q)
        || String(trade.status || '').toLowerCase().includes(q));
    const col = sortColumns.find((c) => c.key === sortKey) || sortColumns[0];
    const sorted = [...matched].sort((a, b) => {
      const cmp = compareTradeField(a.trade, b.trade, col.key, col.type);
      if (cmp !== 0) return sortDir === 'asc' ? cmp : -cmp;
      return a.index - b.index;
    });
    return sorted;
  }, [trades, filter, sortKey, sortDir, sortColumns]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'trade_date' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // A new filter starts at page 1; saving, adding or removing trades keeps your page.
  useEffect(() => { setPage(0); }, [filter]);
  useEffect(() => { setPage((p) => Math.min(p, pageCount - 1)); }, [pageCount]);

  const updateTrade = (index, patch) => {
    onChange(trades.map((t, i) => {
      if (i !== index) return t;
      // Editing a row re-attributes it to a manual touch (mirrors the points editor).
      const merged = { ...t, ...patch, source: 'manual' };
      if ('net_pnl' in patch) return applyPnlDerivedFields(merged, baseCapital);
      if ('net_roi' in patch) return applyRoiDerivedFields(merged, baseCapital);
      return merged;
    }));
  };

  // Marking a trade live or backtest is a label, not a data edit: the source stays.
  const updateTradeSegment = (index, segment) => {
    onChange(trades.map((t, i) => (i === index ? { ...t, segment: segment || null } : t)));
  };

  const removeTrade = (index) => {
    onChange(trades.filter((_, i) => i !== index));
  };

  const addTrade = () => {
    const trade_date = newTrade.trade_date.trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(trade_date)) {
      onAddRejected?.('Enter a valid trade date (YYYY-MM-DD) before adding.');
      return;
    }

    let row;
    const sharedFields = {
      trade_date,
      symbol: newTrade.symbol.trim() || defaultSymbol || '',
      position: newTrade.position || null,
      volume: Number(newTrade.volume) || 0,
      entry_price: newTrade.entry_price === '' ? null : Number(newTrade.entry_price),
      exit_price: newTrade.exit_price === '' ? null : Number(newTrade.exit_price),
      source: 'manual',
    };
    const net_pnl = Number(newTrade.net_pnl);
    const net_roi = Number(newTrade.net_roi);

    if (isVectorEquity) {
      if (Number.isFinite(net_pnl)) {
        row = applyPnlDerivedFields({ ...sharedFields, net_pnl }, baseCapital);
      } else if (Number.isFinite(net_roi)) {
        row = applyRoiDerivedFields({ ...sharedFields, net_roi }, baseCapital);
      } else {
        onAddRejected?.('Enter Net P&L ($) or Net P&L (%) before adding.');
        return;
      }
    } else {
      if (!Number.isFinite(net_pnl)) {
        onAddRejected?.('Enter Net P&L before adding.');
        return;
      }
      row = applyPnlDerivedFields({
        ...sharedFields,
        status: newTrade.status,
        net_pnl,
      }, baseCapital);
    }

    const next = [...trades, row].sort((a, b) => a.trade_date.localeCompare(b.trade_date));
    onChange(next);
    onAddRejected?.(null);
    setNewTrade({
      trade_date: '', symbol: '', position: '', status: 'Win', volume: '', entry_price: '', exit_price: '', net_pnl: '', net_roi: '',
    });
  };

  const tradeNumbers = useMemo(() => {
    const ordered = [...trades]
      .map((trade, index) => ({ trade, index }))
      .sort((a, b) => {
        if (a.trade.trade_date !== b.trade.trade_date) {
          return a.trade.trade_date.localeCompare(b.trade.trade_date);
        }
        return (a.trade.sort_index || 0) - (b.trade.sort_index || 0);
      });
    const map = new Map();
    ordered.forEach(({ index }, i) => map.set(index, i + 1));
    return map;
  }, [trades]);

  const renderTradeCell = (col, row, index) => {
    switch (col.key) {
      case 'trade_date':
        return (
          <input
            type="date"
            value={row.trade_date}
            onChange={(e) => updateTrade(index, { trade_date: e.target.value })}
          />
        );
      case 'symbol':
        return (
          <input
            type="text"
            value={row.symbol || ''}
            placeholder={defaultSymbol || 'AAPL'}
            onChange={(e) => updateTrade(index, { symbol: e.target.value })}
          />
        );
      case 'position':
        return (
          <select
            value={row.position || ''}
            onChange={(e) => updateTrade(index, { position: e.target.value || null })}
          >
            <option value="">—</option>
            <option value="Long">Long</option>
            <option value="Short">Short</option>
          </select>
        );
      case 'status':
        if (isVectorEquity) {
          return (
            <span className={row.status === 'Win' ? 'pos' : row.status === 'Loss' ? 'neg' : ''}>
              {row.status || '—'}
            </span>
          );
        }
        return (
          <select
            value={row.status || 'Win'}
            onChange={(e) => updateTrade(index, { status: e.target.value })}
          >
            <option value="Win">Win</option>
            <option value="Loss">Loss</option>
          </select>
        );
      case 'volume':
        return (
          <input
            type="number"
            step="1"
            value={row.volume ?? 0}
            onChange={(e) => updateTrade(index, { volume: Number(e.target.value) })}
          />
        );
      case 'entry_price':
        return (
          <input
            type="number"
            step="0.01"
            value={row.entry_price ?? ''}
            onChange={(e) => updateTrade(index, { entry_price: e.target.value === '' ? null : Number(e.target.value) })}
          />
        );
      case 'exit_price':
        return (
          <input
            type="number"
            step="0.01"
            value={row.exit_price ?? ''}
            onChange={(e) => updateTrade(index, { exit_price: e.target.value === '' ? null : Number(e.target.value) })}
          />
        );
      case 'net_pnl':
        return (
          <input
            type="number"
            step="0.01"
            value={row.net_pnl ?? ''}
            onChange={(e) => updateTrade(index, { net_pnl: Number(e.target.value) })}
          />
        );
      case 'net_roi':
        if (isVectorEquity) {
          return (
            <input
              type="number"
              step="0.01"
              value={row.net_roi ?? ''}
              onChange={(e) => updateTrade(index, { net_roi: Number(e.target.value) })}
            />
          );
        }
        return (
          <span className={row.net_roi > 0 ? 'pos' : row.net_roi < 0 ? 'neg' : ''}>
            {fmtPct(row.net_roi)}
          </span>
        );
      case 'segment':
        // Live-feed rows are rebuilt from the feed, so they show what it sent
        // (blank when it sends nothing) instead of an edit that wouldn't stick.
        if (isFeedTradeSource(row.source)) {
          return (
            <span
              className={`admin-scenario-segment admin-scenario-segment--${row.segment || 'none'}`}
              title="Set by the live feed"
            >
              {row.segment === 'live' ? 'Live' : row.segment === 'backtest' ? 'Backtest' : ''}
            </span>
          );
        }
        return (
          <select
            className={`admin-scenario-segment admin-scenario-segment--${row.segment || 'none'}`}
            aria-label={`Live or backtest for ${row.trade_date}`}
            value={row.segment || ''}
            onChange={(e) => updateTradeSegment(index, e.target.value)}
          >
            <option value="">—</option>
            <option value="live">Live</option>
            <option value="backtest">Backtest</option>
          </select>
        );
      case 'source':
        return (
          <span className={`admin-scenario-source admin-scenario-source--${(row.source || 'unknown')}`}>
            {sourceLabel(row.source)}
          </span>
        );
      default:
        return row[col.key] ?? '—';
    }
  };

  return (
    <>
      <div className="admin-scenario-actions">
        <input
          type="search"
          className="admin-scenario-filter"
          placeholder={isVectorEquity ? 'Filter by exit date or symbol…' : 'Filter by date or symbol…'}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <span>{filtered.length} trade{filtered.length === 1 ? '' : 's'}</span>
      </div>
      <div className="admin-scenario-table-wrap admin-scenario-table-wrap--wide">
        <table className="admin-scenario-table admin-scenario-table--trades">
          <thead>
            <tr>
              {isVectorEquity ? <th className="num">#</th> : null}
              {sortColumns.map((col) => (
                <th
                  key={col.key}
                  className={col.type === 'number' ? 'num sortable' : 'sortable'}
                >
                  <button
                    type="button"
                    className="admin-scenario-sort-btn"
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}
                    {sortIndicator(col.key)}
                  </button>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {pageRows.map(({ trade: row, index }) => (
                <tr key={row.id != null ? `id-${row.id}` : `${row.trade_date}-${index}`}>
                  {isVectorEquity ? (
                    <td className="num admin-scenario-trade-num">{tradeNumbers.get(index) ?? '—'}</td>
                  ) : null}
                  {sortColumns.map((col) => (
                    <td
                      key={col.key}
                      className={col.type === 'number' || col.key === 'net_roi' ? 'num' : undefined}
                    >
                      {renderTradeCell(col, row, index)}
                    </td>
                  ))}
                  <td>
                    <button
                      type="button"
                      className="admin-sheet-btn admin-sheet-btn-danger"
                      onClick={() => removeTrade(index)}
                    >
                      Remove
                    </button>
                  </td>
                </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePager page={page} pageCount={pageCount} onPage={setPage} />
      <div className="admin-scenario-add-row">
        <label>
          <span>{isVectorEquity ? 'Exit date' : 'Trade date'}</span>
          <input type="date" value={newTrade.trade_date} onChange={(e) => setNewTrade((p) => ({ ...p, trade_date: e.target.value }))} />
        </label>
        <label>
          <span>Symbol</span>
          <input
            type="text"
            value={newTrade.symbol}
            onChange={(e) => setNewTrade((p) => ({ ...p, symbol: e.target.value }))}
            placeholder={defaultSymbol || (isVectorEquity ? 'AAPL' : 'NQM26')}
          />
        </label>
        <label>
          <span>Side</span>
          <select value={newTrade.position} onChange={(e) => setNewTrade((p) => ({ ...p, position: e.target.value }))}>
            <option value="">—</option>
            <option value="Long">Long</option>
            <option value="Short">Short</option>
          </select>
        </label>
        {!isVectorEquity ? (
          <label>
            <span>Status</span>
            <select value={newTrade.status} onChange={(e) => setNewTrade((p) => ({ ...p, status: e.target.value }))}>
              <option value="Win">Win</option>
              <option value="Loss">Loss</option>
            </select>
          </label>
        ) : null}
        <label>
          <span>Volume</span>
          <input type="number" step="1" value={newTrade.volume} onChange={(e) => setNewTrade((p) => ({ ...p, volume: e.target.value }))} />
        </label>
        <label>
          <span>Entry price</span>
          <input type="number" step="0.01" value={newTrade.entry_price} onChange={(e) => setNewTrade((p) => ({ ...p, entry_price: e.target.value }))} />
        </label>
        <label>
          <span>Exit price</span>
          <input type="number" step="0.01" value={newTrade.exit_price} onChange={(e) => setNewTrade((p) => ({ ...p, exit_price: e.target.value }))} />
        </label>
        <label>
          <span>{isVectorEquity ? 'Net P&L ($)' : 'Net P&L'}</span>
          <input type="number" step="0.01" value={newTrade.net_pnl} onChange={(e) => setNewTrade((p) => ({ ...p, net_pnl: e.target.value }))} />
        </label>
        {isVectorEquity ? (
          <label>
            <span>Net P&L (%)</span>
            <input type="number" step="0.01" value={newTrade.net_roi} onChange={(e) => setNewTrade((p) => ({ ...p, net_roi: e.target.value }))} />
          </label>
        ) : null}
        <button type="button" className="admin-sheet-btn" onClick={addTrade}>Add trade</button>
      </div>
    </>
  );
}

function EquityPointsEditor({ points, onChange, defaultSource = null }) {
  const [filter, setFilter] = useState('');
  const [page, setPage] = useState(0);
  const [newDate, setNewDate] = useState('');
  const [newEquity, setNewEquity] = useState('');
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc'); // latest day first by default

  // Daily P&L / return are day-over-day, so derive on CHRONOLOGICAL order first;
  // the display sort below is applied afterward and never affects those values.
  const tradeRows = useMemo(() => {
    const chrono = [...points].sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return deriveTradeRows(chrono, defaultSource);
  }, [points, defaultSource]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const matched = !q ? tradeRows : tradeRows.filter((r) => r.date.includes(q));
    const col = EQUITY_POINT_SORT_COLUMNS.find((c) => c.key === sortKey) || EQUITY_POINT_SORT_COLUMNS[0];
    return [...matched].sort((a, b) => {
      const cmp = compareTradeField(a, b, col.key, col.type);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [tradeRows, filter, sortKey, sortDir]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'date' ? 'desc' : 'asc');
    }
    setPage(0);
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  // A new filter starts at page 1. Saving, adding or removing a row keeps the
  // page you're on (pulled back only if that page no longer exists).
  useEffect(() => {
    setPage(0);
  }, [filter]);
  useEffect(() => {
    setPage((p) => Math.min(p, pageCount - 1));
  }, [pageCount]);

  const updatePoint = (date, patch) => {
    // Any in-table edit marks the row as manually entered.
    onChange(points.map((p) => (p.date === date ? { ...p, ...patch, source: 'manual' } : p)));
  };

  const removePoint = (date) => {
    onChange(points.filter((p) => p.date !== date));
  };

  // Marking a day live or backtested isn't an edit of its balance, so the
  // row keeps its source (CSV / webhook).
  const updateSegment = (date, segment) => {
    onChange(points.map((p) => {
      if (p.date !== date) return p;
      const { segment: _old, ...rest } = p;
      return segment ? { ...rest, segment } : rest;
    }));
  };

  const addPoint = () => {
    const date = newDate.trim();
    const equity = Number(newEquity);
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) return;
    if (!Number.isFinite(equity)) return;
    const without = points.filter((p) => p.date !== date);
    const next = [...without, { date, equity, source: 'manual' }].sort((a, b) => a.date.localeCompare(b.date));
    onChange(next);
    setNewDate('');
    setNewEquity('');
  };

  return (
    <>
      <div className="admin-scenario-actions">
        <input
          type="search"
          className="admin-scenario-filter"
          placeholder="Filter by date…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        <span>{filtered.length} row{filtered.length === 1 ? '' : 's'}</span>
      </div>
      <div className="admin-scenario-table-wrap">
        <table className="admin-scenario-table">
          <thead>
            <tr>
              {EQUITY_POINT_SORT_COLUMNS.map((col) => (
                <th key={col.key} className={col.type === 'number' ? 'num sortable' : 'sortable'}>
                  <button
                    type="button"
                    className="admin-scenario-sort-btn"
                    onClick={() => toggleSort(col.key)}
                  >
                    {col.label}
                    {sortIndicator(col.key)}
                  </button>
                </th>
              ))}
              <th />
            </tr>
          </thead>
          <tbody>
            {pageRows.map((row) => (
              <tr key={row.date}>
                <td>
                  <input
                    type="date"
                    value={row.date}
                    onChange={(e) => {
                      const nextDate = e.target.value;
                      if (!nextDate || nextDate === row.date) return;
                      const others = points.filter((p) => p.date !== row.date);
                      if (others.some((p) => p.date === nextDate)) return;
                      updatePoint(row.date, { date: nextDate });
                    }}
                  />
                </td>
                <td className="num">
                  <input
                    type="number"
                    step="0.01"
                    value={row.equity}
                    onChange={(e) => updatePoint(row.date, { equity: Number(e.target.value) })}
                  />
                </td>
                <td className={`num ${row.dailyPnl > 0 ? 'pos' : row.dailyPnl < 0 ? 'neg' : ''}`}>
                  {fmtMoney(row.dailyPnl)}
                </td>
                <td className={`num ${row.dailyReturnPct > 0 ? 'pos' : row.dailyReturnPct < 0 ? 'neg' : ''}`}>
                  {fmtPct(row.dailyReturnPct)}
                </td>
                <td>
                  <select
                    className={`admin-scenario-segment admin-scenario-segment--${row.segment || 'none'}`}
                    value={row.segment || ''}
                    aria-label={`Live or backtest for ${row.date}`}
                    onChange={(e) => updateSegment(row.date, e.target.value || null)}
                  >
                    <option value="">—</option>
                    <option value="live">Live</option>
                    <option value="backtest">Backtest</option>
                  </select>
                </td>
                <td className={`admin-scenario-source admin-scenario-source--${(row.source || 'unknown')}`}>
                  {sourceLabel(row.source)}
                </td>
                <td>
                  <button
                    type="button"
                    className="admin-sheet-btn admin-sheet-btn-danger"
                    onClick={() => removePoint(row.date)}
                    disabled={points.length <= 2}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TablePager page={page} pageCount={pageCount} onPage={setPage} />
      <div className="admin-scenario-add-row">
        <label>
          <span>New date</span>
          <input type="date" value={newDate} onChange={(e) => setNewDate(e.target.value)} />
        </label>
        <label>
          <span>Equity ($)</span>
          <input type="number" step="0.01" value={newEquity} onChange={(e) => setNewEquity(e.target.value)} />
        </label>
        <button type="button" className="admin-sheet-btn" onClick={addPoint}>Add row</button>
      </div>
    </>
  );
}

function FuturesGridEditor({ rawData, onChange }) {
  const headerRows = rawData.slice(0, 2);
  const dataRows = rawData.slice(2);

  const updateCell = (rowIndex, colIndex, value) => {
    const next = rawData.map((row, ri) => {
      if (ri !== rowIndex + 2) return row;
      const copy = [...row];
      while (copy.length < 7) copy.push('');
      copy[colIndex] = value;
      return copy;
    });
    onChange(next);
  };

  const addWeekRow = () => {
    onChange([...rawData, ['', '', '', '', '', '', '']]);
  };

  const removeWeekRow = (rowIndex) => {
    if (dataRows.length <= 1) return;
    const next = [...headerRows, ...dataRows.filter((_, i) => i !== rowIndex)];
    onChange(next);
  };

  return (
    <>
      <div className="admin-scenario-meta">
        <span>Grid format: row 0 = strategy name, row 1 = weekday headers, row 2+ = weekly P&L.</span>
      </div>
      <div className="admin-scenario-table-wrap">
        <table className="admin-scenario-table">
          <thead>
            <tr>
              <th>Week</th>
              <th className="num">Mon</th>
              <th className="num">Tue</th>
              <th className="num">Wed</th>
              <th className="num">Thu</th>
              <th className="num">Fri</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {dataRows.map((row, rowIndex) => (
              <tr key={`week-${rowIndex}`}>
                <td>
                  <input
                    type="text"
                    value={row[0] || ''}
                    onChange={(e) => updateCell(rowIndex, 0, e.target.value)}
                    placeholder="MM.DD.YYYY"
                  />
                </td>
                {[2, 3, 4, 5, 6].map((col) => (
                  <td key={col} className="num">
                    <input
                      type="text"
                      value={row[col] || ''}
                      onChange={(e) => updateCell(rowIndex, col, e.target.value)}
                      placeholder="$0"
                    />
                  </td>
                ))}
                <td>
                  <button
                    type="button"
                    className="admin-sheet-btn admin-sheet-btn-danger"
                    onClick={() => removeWeekRow(rowIndex)}
                    disabled={dataRows.length <= 1}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-scenario-actions">
        <button type="button" className="admin-sheet-btn" onClick={addWeekRow}>Add week row</button>
      </div>
    </>
  );
}

function PortfolioConfigEditor({ portfolioKey, config, onSave }) {
  const [equityPct, setEquityPct] = useState(config?.equityPct ?? 100);
  const [leverage, setLeverage] = useState(config?.leverage ?? 1);
  const [legs, setLegs] = useState(config?.legs || []);

  useEffect(() => {
    setEquityPct(config?.equityPct ?? 100);
    setLeverage(config?.leverage ?? 1);
    setLegs(config?.legs || []);
  }, [config]);

  const updateLeg = (ticker, patch) => {
    setLegs((prev) => prev.map((l) => (l.ticker === ticker ? { ...l, ...patch } : l)));
  };

  return (
    <div className="admin-scenario-portfolio">
      <div className="admin-scenario-meta">
        <span><strong>Portfolio:</strong> {PORTFOLIO_LABELS[portfolioKey] || portfolioKey}</span>
      </div>
      <div className="admin-equity-field">
        <span>Equity %</span>
        <input type="number" min="0" max="200" value={equityPct} onChange={(e) => setEquityPct(Number(e.target.value))} />
      </div>
      <div className="admin-equity-field">
        <span>Leverage ×</span>
        <input type="number" min="0" max="5" step="0.05" value={leverage} onChange={(e) => setLeverage(Number(e.target.value))} />
      </div>
      <div className="admin-scenario-table-wrap">
        <table className="admin-scenario-table">
          <thead>
            <tr>
              <th>Enabled</th>
              <th>Stock</th>
              <th className="num">Alloc %</th>
              <th className="num">Equity / Trade ($)</th>
            </tr>
          </thead>
          <tbody>
            {legs.map((leg) => (
              <tr key={leg.ticker}>
                <td>
                  <input
                    type="checkbox"
                    checked={leg.enabled}
                    onChange={(e) => updateLeg(leg.ticker, { enabled: e.target.checked })}
                  />
                </td>
                <td>{leg.ticker}</td>
                <td className="num">
                  <input
                    type="number"
                    value={leg.allocation}
                    onChange={(e) => updateLeg(leg.ticker, { allocation: Number(e.target.value) })}
                  />
                </td>
                <td className="num">
                  <input
                    type="number"
                    value={leg.equityPerTrade}
                    onChange={(e) => updateLeg(leg.ticker, { equityPerTrade: Number(e.target.value) })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-scenario-actions">
        <button
          type="button"
          className="admin-sheet-btn admin-sheet-btn-primary"
          onClick={() => onSave({ portfolioKey, equityPct, leverage, legs })}
        >
          Save portfolio config
        </button>
      </div>
    </div>
  );
}

function MasterStratEditor({ sleeves, strategyOptions, onSave }) {
  const [rows, setRows] = useState(sleeves || []);

  useEffect(() => {
    setRows(sleeves || []);
  }, [sleeves]);

  const updateSleeve = (idx, patch) => {
    setRows((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const toggleDay = (idx, day) => {
    setRows((prev) => prev.map((s, i) => {
      if (i !== idx) return s;
      const days = Array.isArray(s.days) ? [...s.days] : [];
      const has = days.includes(day);
      return { ...s, days: has ? days.filter((d) => d !== day) : [...days, day] };
    }));
  };

  return (
    <>
      <div className="admin-scenario-meta">
        <span>Org-wide {getEntityDisplayName()} Master Strat sleeve composition.</span>
      </div>
      {rows.map((sleeve, idx) => (
        <div key={idx} className="admin-scenario-master-sleeve">
          <select
            className="admin-sheet-master-strategy"
            value={sleeve.strategy || ''}
            onChange={(e) => updateSleeve(idx, { strategy: e.target.value })}
          >
            <option value="">Select strategy…</option>
            {(strategyOptions || []).map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
          <div className="admin-sheet-master-days">
            {MASTER_WEEKDAYS.map((d) => (
              <button
                key={d.key}
                type="button"
                className={`dashboard-combo-day${(sleeve.days || []).includes(d.key) ? ' is-on' : ''}`}
                onClick={() => toggleDay(idx, d.key)}
              >
                {d.short}
              </button>
            ))}
          </div>
          <input
            type="number"
            min="1"
            max="100"
            value={sleeve.size ?? 1}
            onChange={(e) => updateSleeve(idx, { size: Number(e.target.value) })}
          />
          <button
            type="button"
            className="admin-sheet-btn admin-sheet-btn-danger"
            onClick={() => setRows((prev) => prev.filter((_, i) => i !== idx))}
            disabled={rows.length <= 1}
          >
            Remove
          </button>
        </div>
      ))}
      <div className="admin-scenario-actions">
        <button
          type="button"
          className="admin-sheet-btn"
          onClick={() => setRows((prev) => [...prev, { strategy: '', days: [], size: 1 }])}
        >
          Add sleeve
        </button>
        <button type="button" className="admin-sheet-btn admin-sheet-btn-primary" onClick={() => onSave(rows)}>
          Save Master Strat
        </button>
      </div>
    </>
  );
}

async function copyWebhookUrl(text, onDone) {
  const value = String(text || '');
  if (!value) return;
  try {
    await navigator.clipboard.writeText(value);
    onDone('Copied');
  } catch {
    onDone('Select and copy manually');
  }
}

function WebhookTokenPanel({ webhook, disabled, onMessage, onError }) {
  const [revealedUrl, setRevealedUrl] = useState('');
  const [copyStatus, setCopyStatus] = useState('');
  const [regenerating, setRegenerating] = useState(false);

  useEffect(() => {
    setRevealedUrl('');
    setCopyStatus('');
  }, [webhook?.slug]);

  useEffect(() => {
    if (!copyStatus) return undefined;
    const t = setTimeout(() => setCopyStatus(''), 2000);
    return () => clearTimeout(t);
  }, [copyStatus]);

  if (!webhook?.slug) return null;

  const regenerate = async () => {
    if (!window.confirm(
      'Generate a new webhook token? The old token will stop working immediately.',
    )) return;
    setRegenerating(true);
    if (onError) onError(null);
    try {
      const res = await fetch(
        API_ENDPOINTS.adminScenarioRegenerateWebhookToken(webhook.slug),
        { method: 'POST', headers: authHeaders() },
      );
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to regenerate token');
      setRevealedUrl(data.webhookUrl);
      if (onMessage) {
        onMessage('New webhook token generated — copy the full URL below and send it to your client.');
      }
    } catch (e) {
      if (onError) onError(e.message);
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div className="admin-scenario-webhook-panel" role="region" aria-label="Webhook token">
      <div className="admin-scenario-webhook-head">
        <strong>Live webhook</strong>
        <span>
          {webhook.hasToken ? 'token configured' : 'no token yet'}
          {webhook.kairosSymbol ? ` · symbol ${webhook.kairosSymbol}` : ''}
        </span>
      </div>
      <p className="admin-scenario-webhook-base">
        Base URL (no secret): <code>{webhook.url}</code>
      </p>
      <p className="admin-scenario-webhook-note">
        The secret token is only shown once at creation. We store a hash, so the old token
        cannot be retrieved. Regenerate to issue a new full URL.
      </p>
      <div className="admin-scenario-actions">
        <button
          type="button"
          className="admin-sheet-btn"
          onClick={regenerate}
          disabled={disabled || regenerating}
        >
          {regenerating ? 'Generating…' : (webhook.hasToken ? 'Regenerate token' : 'Generate token')}
        </button>
      </div>
      {revealedUrl ? (
        <div className="admin-scenario-webhook-reveal">
          <span>Full webhook URL (includes token)</span>
          <div className="admin-scenario-webhook-copy-row">
            <code>{revealedUrl}</code>
            <button
              type="button"
              className="admin-sheet-btn"
              onClick={() => copyWebhookUrl(revealedUrl, setCopyStatus)}
            >
              <IconCopy size={14} />
              {copyStatus || 'Copy'}
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default function AdminScenarioData({ onNavigate }) {
  const { isAdmin } = useAuth();
  const [catalog, setCatalog] = useState(null);
  const [selectedKey, setSelectedKey] = useState('');
  const selectedKeyRef = useRef(selectedKey);
  selectedKeyRef.current = selectedKey;

  // Remember the last-edited strategy across visits (per browser).
  useEffect(() => {
    if (!selectedKey) return;
    try { localStorage.setItem(SCENARIO_DATA_SELECTED_KEY, selectedKey); } catch (_e) { /* no-op */ }
  }, [selectedKey]);
  const [loading, setLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [equityMeta, setEquityMeta] = useState(null);
  const [equityPoints, setEquityPoints] = useState([]);
  const [equityLiveOverlay, setEquityLiveOverlay] = useState(null);
  const [tradeJournalMeta, setTradeJournalMeta] = useState(null);
  const [journalTrades, setJournalTrades] = useState([]);
  const journalTradesRef = useRef(journalTrades);
  journalTradesRef.current = journalTrades;
  const loadSeqRef = useRef(0);
  const [futuresMeta, setFuturesMeta] = useState(null);
  const [futuresGrid, setFuturesGrid] = useState([]);
  const [portfolioConfigs, setPortfolioConfigs] = useState(null);
  const [masterStrat, setMasterStrat] = useState(null);
  const [ntConnectorMeta, setNtConnectorMeta] = useState(null);
  const [ntConnectorTrades, setNtConnectorTrades] = useState([]);
  const [instrumentClass, setInstrumentClass] = useState('futures');
  const [displaySymbol, setDisplaySymbol] = useState('');
  const [instrumentSaving, setInstrumentSaving] = useState(false);
  // True when nothing is saved and the class came from the strategy's name.
  const [instrumentInferred, setInstrumentInferred] = useState(false);

  /**
   * Registry webhook stubs whose curve actually lives on an equity-strategy row.
   *
   * A daily-equity webhook produces TWO catalog entries: the registry scenario
   * (type 'webhook', which can only render a banner) and the equity strategy
   * holding the points. Offering both means picking the wrong one strands you
   * on "Live webhook strategy. Data is ingested automatically." with no table,
   * even though every point is editable one entry further down the list.
   *
   * The deep-link path already prefers the equity row; this applies the same
   * rule to the dropdown so the dead end is not reachable at all.
   */
  const shadowedWebhookSlugs = useMemo(() => new Set(
    (catalog?.equityStrategies || [])
      .map((r) => r.webhook?.slug)
      .filter(Boolean),
  ), [catalog]);

  const options = useMemo(() => {
    if (!catalog) return [];
    const out = [];
    (catalog.registryScenarios || []).forEach((o) => {
      if (!isAdmin && (o.type === 'nt-connector' || o.dataSourceType === 'nt_connector')) return;
      if (o.type === 'webhook' && shadowedWebhookSlugs.has(o.slug)) return;
      out.push(o);
    });
    (catalog.equityStrategies || []).forEach((o) => out.push(o));
    (catalog.futuresStrategies || []).forEach((o) => out.push(o));
    if (isAdmin) {
      (catalog.ntConnectorAccounts || []).forEach((o) => out.push(o));
    }
    (catalog.configs || []).forEach((o) => out.push(o));
    return out;
  }, [catalog, isAdmin, shadowedWebhookSlugs]);

  /**
   * A stub selection (restored from localStorage, or saved before the dedupe
   * above existed) would otherwise leave the editor blank, since the option no
   * longer exists. Move it to the equity row that owns the curve.
   */
  useEffect(() => {
    if (!catalog || !selectedKey.startsWith('webhook:')) return;
    const slug = selectedKey.slice('webhook:'.length);
    if (!shadowedWebhookSlugs.has(slug)) return;
    const owner = (catalog.equityStrategies || []).find((r) => r.webhook?.slug === slug);
    if (owner) setSelectedKey(`${owner.type}:${owner.key}`);
  }, [catalog, selectedKey, shadowedWebhookSlugs]);

  const selected = useMemo(
    () => options.find((o) => `${o.type}:${o.key}` === selectedKey) || null,
    [options, selectedKey],
  );

  /**
   * quiet: refresh the list in place (after Save instrument, Refresh list) without
   * hiding the editor or changing the selected strategy, so unsaved edits stay.
   */
  const loadCatalog = useCallback(async ({ quiet = false } = {}) => {
    if (!quiet) setLoading(true);
    if (!quiet) setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioCatalog, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load catalog');
      setCatalog(data);
      if (quiet && selectedKeyRef.current) return;
      const params = new URLSearchParams(window.location.search);
      const slugParam = params.get('scenario');
      // ?strategy=<data name>: Strategies page rows that have data but no record.
      const strategyParam = params.get('strategy');
      if (strategyParam) {
        params.delete('strategy');
        const rest = params.toString();
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
        const wanted = strategyParam.trim().toLowerCase();
        const match = [
          ...(data.futuresStrategies || []),
          ...(data.equityStrategies || []),
          ...(data.configs || []),
          ...(data.registryScenarios || []),
        ].find((o) => String(o.key || '').trim().toLowerCase() === wanted);
        if (match) {
          setSelectedKey(`${match.type}:${match.key}`);
          return;
        }
      }
      if (slugParam) {
        // A deep link (Strategies → Edit data) picks the first strategy only. Drop
        // it from the address bar so a reload or remount restores the strategy
        // picked in the dropdown since (saved below), not the one linked to.
        params.delete('scenario');
        const rest = params.toString();
        window.history.replaceState(window.history.state, '', `${window.location.pathname}${rest ? `?${rest}` : ''}${window.location.hash}`);
        // Kairos daily webhooks store curve data on the equity-strategy row.
        // Create Strategy links here with ?scenario=<slug>, so open that editor
        // instead of the registry webhook stub (banners only, no table).
        const equityForWebhookSlug = (data.equityStrategies || []).find(
          (r) => r.webhook?.slug === slugParam,
        );
        if (equityForWebhookSlug) {
          setSelectedKey(`${equityForWebhookSlug.type}:${equityForWebhookSlug.key}`);
          return;
        }
        const equityMatch = (data.equityStrategies || []).find(
          (r) => String(r.key).toLowerCase() === slugParam.toLowerCase(),
        );
        if (equityMatch) {
          setSelectedKey(`${equityMatch.type}:${equityMatch.key}`);
          return;
        }
        const registryMatch = (data.registryScenarios || []).find((r) => r.slug === slugParam);
        if (registryMatch) {
          setSelectedKey(`${registryMatch.type}:${registryMatch.key}`);
          return;
        }
      }
      const all = [
        ...(data.configs || []),
        ...(data.registryScenarios || []),
        ...(data.equityStrategies || []),
        ...(data.futuresStrategies || []),
        ...(data.ntConnectorAccounts || []),
      ];
      // Restore the last strategy this browser was editing, if it still exists.
      try {
        const stored = localStorage.getItem(SCENARIO_DATA_SELECTED_KEY);
        if (stored && all.some((o) => `${o.type}:${o.key}` === stored)) {
          setSelectedKey(stored);
          return;
        }
      } catch (_e) { /* localStorage unavailable — fall through to default */ }
      const first = all[0];
      if (first) setSelectedKey(`${first.type}:${first.key}`);
    } catch (e) {
      setError(e.message);
      if (!quiet) setCatalog(null);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  const loadScenarioData = useCallback(async (item, { soft = false } = {}) => {
    if (!item) return;
    const seq = ++loadSeqRef.current;
    const stale = () => seq !== loadSeqRef.current;
    if (!soft) {
      setDataLoading(true);
      setError(null);
      setMessage(null);
      setEquityMeta(null);
      setEquityPoints([]);
      setEquityLiveOverlay(null);
      setTradeJournalMeta(null);
      setJournalTrades([]);
      setFuturesMeta(null);
      setFuturesGrid([]);
      setPortfolioConfigs(null);
      setMasterStrat(null);
      setNtConnectorMeta(null);
      setNtConnectorTrades([]);
    }
    try {
      if (item.type === 'nt-connector') {
        const res = await fetch(API_ENDPOINTS.adminScenarioNtConnector(item.key), { headers: authHeaders() });
        const data = await res.json();
        if (stale()) return; // a newer load (another strategy) took over
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load NT connector trades');
        setNtConnectorMeta(data);
        setNtConnectorTrades(data.trades || []);
      } else if (item.type === 'webhook') {
        setMessage(`Live webhook strategy “${item.label}”. The equity curve updates automatically when alerts arrive. Copy the webhook URL from Settings → Create Strategy.`);
      } else if (item.type === 'trade-journal') {
        const res = await fetch(API_ENDPOINTS.adminScenarioTrades(item.key), { headers: authHeaders() });
        const data = await res.json();
        if (stale()) return; // a newer load (another strategy) took over
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load trades');
        setTradeJournalMeta(data);
        setJournalTrades(data.trades || []);
        if (data.liveOverlay?.mirrored) {
          setMessage(
            `${data.liveOverlay.mirrorLiveLabel || 'Live feed mirrored from a linked strategy'} — `
            + `${data.liveOverlay.webhookTradeCount || 0} NT8 webhook trade(s) included. Updates sync automatically.`,
          );
        } else if (data.liveOverlay?.active && data.liveOverlay.webhookTradeCount > 0) {
          setMessage(`Includes ${data.liveOverlay.webhookTradeCount} NT8 webhook trade(s) merged at read time.`);
        }
        if (data.liveOverlay?.readsFrom) {
          setError(
            `Heads up: Results, the Overview curve and the Simulator read this strategy's live data from “${data.liveOverlay.readsFrom}”, `
            + `not from “${item.key}”. Trades you save here are kept, but those pages won't show them. Edit “${data.liveOverlay.readsFrom}” instead, or ask support to point this strategy at its own data.`,
          );
        }
        if (data.backfill?.seeded && data.backfill.source === 'sheet-grid') {
          setMessage(
            `Seeded ${data.backfill.tradeCount} day(s) from the Results Spreadsheet grid. You can edit and save below.`,
          );
        } else if (data.backfill?.seeded) {
          setMessage(
            `Loaded ${data.backfill.tradeCount} trades from ingested CSV (${data.backfill.sourceFile}). You can edit and save below.`,
          );
        } else if ((data.trades || []).length === 0 && data.backfill?.reason === 'csv-not-found') {
          setError('No data found for this strategy — no journal, no ingested CSV, and no Results Spreadsheet days. Import a TradesViz CSV file to get started.');
        }
      } else if (item.type === 'equity') {
        const res = await fetch(API_ENDPOINTS.adminScenarioEquity(item.key), { headers: authHeaders() });
        const data = await res.json();
        if (stale()) return; // a newer load (another strategy) took over
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load equity data');
        setEquityMeta(data.strategy);
        setEquityPoints(data.points || []);
        setEquityLiveOverlay(data.liveOverlay || null);
        if (!soft && data.liveOverlay?.merged && data.liveOverlay.webhookPointCount > 0) {
          setMessage(
            `Includes ${data.liveOverlay.webhookPointCount} NT8 webhook row(s). `
            + 'Edit or remove them like any other row, then click Save equity curve to persist.',
          );
        }
      } else if (item.type === 'futures') {
        const res = await fetch(API_ENDPOINTS.adminScenarioFutures(item.key), { headers: authHeaders() });
        const data = await res.json();
        if (stale()) return; // a newer load (another strategy) took over
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load futures data');
        setFuturesMeta(data.snapshot);
        setFuturesGrid(data.snapshot?.rawData || []);
      } else if (item.type === 'portfolio') {
        const res = await fetch(API_ENDPOINTS.adminEquityPortfolioConfig, { headers: authHeaders() });
        const data = await res.json();
        if (stale()) return; // a newer load (another strategy) took over
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load portfolio config');
        setPortfolioConfigs(data.configs || {});
      } else if (item.type === 'master-strat') {
        const res = await fetch(API_ENDPOINTS.adminMasterStrat, { headers: authHeaders() });
        const data = await res.json();
        if (stale()) return; // a newer load (another strategy) took over
        if (!res.ok || !data.success) throw new Error(data.error || 'Failed to load Master Strat');
        setMasterStrat(data);
      }
    } catch (e) {
      if (!stale()) setError(e.message);
    } finally {
      if (!soft && !stale()) setDataLoading(false);
    }
  }, []);

  /** Refresh trade journal after save without wiping the table (avoids "page refresh" flash). */
  const refreshJournalTrades = useCallback(async (item) => {
    if (!item || item.type !== 'trade-journal') return;
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioTrades(item.key), { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to reload trades');
      setTradeJournalMeta(data);
      setJournalTrades(data.trades || []);
      return data.trades || [];
    } catch (e) {
      setError(e.message);
      return null;
    }
  }, []);

  useEffect(() => { loadCatalog(); }, [loadCatalog]);

  // Trades changed here but not saved yet (edits, removed rows, partial CSV). They
  // live only in this browser until "Save trades & rebuild curve".
  const journalDirty = useMemo(() => Boolean(
    selected?.type === 'trade-journal' && tradeJournalMeta
      && JSON.stringify(journalTrades) !== JSON.stringify(tradeJournalMeta.trades || []),
  ), [selected, tradeJournalMeta, journalTrades]);
  const journalDirtyRef = useRef(journalDirty);
  journalDirtyRef.current = journalDirty;

  useEffect(() => {
    if (!journalDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [journalDirty]);

  /** Ask before an action that would drop unsaved trade changes. */
  const okToDropUnsaved = useCallback(() => !journalDirtyRef.current || window.confirm(
    'You have unsaved trade changes (edits or added CSV rows).\n\n'
    + 'They will be lost. Click Cancel, then "Save trades & rebuild curve" to keep them.\n\nDiscard them?',
  ), []);

  // Keyed on the strategy, not the `selected` object: a catalog refresh (Save
  // instrument, Refresh list) rebuilds that object, and reloading on it threw away
  // unsaved edits and partial-CSV rows and cleared the "Saved" message.
  const selectedRef = useRef(selected);
  selectedRef.current = selected;
  const hasSelected = Boolean(selected);
  useEffect(() => {
    if (selectedRef.current) loadScenarioData(selectedRef.current);
  }, [selectedKey, hasSelected, loadScenarioData]);

  const loadInstrumentFields = useCallback(async (item) => {
    setInstrumentInferred(false);
    if (!item || !INSTRUMENT_EDITOR_TYPES.has(item.type)) {
      setInstrumentClass('futures');
      setDisplaySymbol('');
      return;
    }
    if (item.instrumentMeta?.displaySymbol) {
      setInstrumentClass(item.instrumentMeta.instrumentClass || 'futures');
      setDisplaySymbol(item.instrumentMeta.displaySymbol);
      setInstrumentInferred(Boolean(item.instrumentMeta.inferred));
      return;
    }
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioInstrument(item.key), { headers: authHeaders() });
      const data = await res.json();
      if (res.ok && data.success && data.instrument) {
        setInstrumentClass(data.instrument.instrumentClass || 'futures');
        setDisplaySymbol(data.instrument.displaySymbol || '');
        setInstrumentInferred(Boolean(data.instrument.inferred));
      } else {
        setInstrumentClass('futures');
        setDisplaySymbol('');
      }
    } catch (_e) {
      setInstrumentClass('futures');
      setDisplaySymbol('');
    }
  }, []);

  useEffect(() => {
    if (selectedRef.current) loadInstrumentFields(selectedRef.current);
  }, [selectedKey, hasSelected, loadInstrumentFields]);

  const saveInstrument = async () => {
    if (!selected || !INSTRUMENT_EDITOR_TYPES.has(selected.type)) return;
    setInstrumentSaving(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioInstrument(selected.key), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ instrumentClass, displaySymbol }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Failed to save instrument settings');
      setMessage(
        `Saved instrument for “${selected.label || selected.key}”: `
        + `${data.instrument.displaySymbol} (${data.instrument.instrumentClass}).`,
      );
      setInstrumentInferred(false);
      await loadCatalog({ quiet: true }); // keeps the table and any unsaved edits
    } catch (e) {
      setError(e.message);
    } finally {
      setInstrumentSaving(false);
    }
  };

  const saveTrades = async () => {
    if (!selected || selected.type !== 'trade-journal') return;
    const conflicts = liveConflictDays(journalTradesRef.current);
    let keepLiveOnOwnedDays = false;
    if (conflicts.length) {
      keepLiveOnOwnedDays = window.confirm(
        `${conflicts.length} day(s) have both your own trades and live NinjaTrader trades: ${conflicts.slice(0, 6).join(', ')}${conflicts.length > 6 ? '…' : ''}.\n\n`
        + 'A day with your own trade replaces that day\'s live trades.\n\n'
        + 'OK = keep the live trades too (they are saved as your own rows).\n'
        + 'Cancel = your trades replace the live ones for those days (use this when your row IS the same trade).',
      );
    }
    const plan = planJournalSave(journalTradesRef.current, tradeJournalMeta?.trades, { keepLiveOnOwnedDays });
    const tradesToSave = plan.trades;
    if (!tradesToSave.length && !plan.removedNt8TradeIds.length) {
      setError('No journal trades to save. Live NT8 webhook rows are read-only — add or edit CSV/manual rows, then save.');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioTrades(selected.key), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({
          trades: tradesToSave,
          removedNt8TradeIds: plan.removedNt8TradeIds,
          baseCapital: tradeJournalMeta?.baseCapital || DEFAULT_BASE,
          // Refused (409) if someone else saved these trades since this page loaded them.
          expectedVersion: tradeJournalMeta?.version || undefined,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      const overlayNote = data.excludedWebhookTrades
        ? ` ${data.excludedWebhookTrades} live NT8 webhook trade(s) removed from this strategy.`
        : (tradesToSave.length < journalTradesRef.current.length
          ? ' Live NT8 rows were kept separate and re-merged on reload.'
          : '');
      setMessage(
        `Saved ${data.tradeCount} trades → ${data.pointCount} equity points (${data.startDate} → ${data.endDate}).`
        + (data.regeneratedWeekdayGrid ? ` ${getResultsNavLabel()} weekday grid updated.` : '')
        + overlayNote,
      );
      const reloaded = await refreshJournalTrades(selected);
      // Never let a saved row disappear silently: say which ones the reload lacks.
      const missing = reloaded ? missingAfterSave(tradesToSave, reloaded) : [];
      if (missing.length) {
        setError(
          `${missing.length} saved trade(s) don't show after reloading: `
          + missing.slice(0, 4).map((t) => `${t.trade_date} ${t.symbol || ''} ${t.net_pnl}`).join('; ')
          + (missing.length > 4 ? '…' : '')
          + '. They are saved to this strategy, but the table reads its trades from elsewhere — see the note above.',
        );
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const reloadFromIngestedCsv = async () => {
    if (!selected || selected.type !== 'trade-journal') return;
    if (!okToDropUnsaved()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioTradesReloadCsv(selected.key), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Reload failed');
      // The reload is already saved on the server: refresh trades AND their version,
      // or the page would show "unsaved changes" and refuse the next save as stale.
      await refreshJournalTrades(selected);
      setMessage(`Reloaded ${data.tradeCount} trades from ${data.sourceFile}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const importTradeCsv = async (csvText, filename = '') => {
    if (!selected || selected.type !== 'trade-journal' || !csvText.trim()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioTradesImport(selected.key), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          csv: csvText,
          filename,
          baseCapital: tradeJournalMeta?.baseCapital || DEFAULT_BASE,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Import failed');
      const formatLabel = tradeImportFormatLabel(data.sourceFormat);
      setMessage(
        `Imported ${data.tradeCount} trades from ${filename ? `"${filename}" (${formatLabel})` : formatLabel} → ${data.pointCount} equity points.`
        + (data.skippedRows ? ` (${data.skippedRows} row(s) skipped.)` : '')
        + (data.regeneratedWeekdayGrid ? ` ${getResultsNavLabel()} weekday grid updated.` : ''),
      );
      await loadScenarioData(selected);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const addPartialTradeCsv = async (csvText, filename = '') => {
    if (!selected || selected.type !== 'trade-journal' || !csvText.trim()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioTradesParseCsv(selected.key), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          csv: csvText,
          filename,
          baseCapital: tradeJournalMeta?.baseCapital || DEFAULT_BASE,
        }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Parse failed');
      const incoming = data.trades || [];
      if (!incoming.length) throw new Error('No trades found in the CSV.');
      const formatLabel = tradeImportFormatLabel(data.sourceFormat);
      // Merge against the table as it is now, then set it. Reading the result out
      // of a setState updater raced React: the updater can run later, so this
      // said "all trades already exist" while the row was in fact added.
      const mergeResult = mergePartialTrades(journalTradesRef.current, incoming);
      journalTradesRef.current = mergeResult.trades;
      setJournalTrades(mergeResult.trades);
      const { addedCount, skippedDupes } = mergeResult;
      if (addedCount < 1) {
        throw new Error('All trades in the CSV already exist in the table (duplicates skipped).');
      }
      setMessage(
        `Added ${addedCount} trade(s) from ${filename ? `"${filename}"` : 'CSV'} (${formatLabel}).`
        + (skippedDupes ? ` ${skippedDupes} duplicate(s) skipped.` : '')
        + (data.skippedRows ? ` (${data.skippedRows} row(s) skipped during parse.)` : '')
        + ' Review the table, then click Save trades & rebuild curve.',
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const onPartialDataFileSelect = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => addPartialTradeCsv(String(reader.result || ''), file.name);
    reader.onerror = () => setError('Could not read the file.');
    reader.readAsText(file);
    e.target.value = '';
  };

  const downloadNormalizedCsv = async () => {
    if (!selected || selected.type !== 'trade-journal' || journalTrades.length < 1) return;
    setSaving(true);
    setError(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioTradesExportCsv(selected.key), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          trades: journalTrades,
          baseCapital: tradeJournalMeta?.baseCapital || DEFAULT_BASE,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Export failed');
      }
      const csv = await res.text();
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const slug = selected.key.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase();
      const filename = parseAttachmentFilename(
        res.headers.get('Content-Disposition'),
        `${slug}_tradesviz_${stamp}.csv`,
      );
      downloadTextAsFile(csv, filename);
      setMessage(`Downloaded normalized CSV (${journalTrades.length} trades).`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveEquity = async () => {
    if (!selected || selected.type !== 'equity') return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioEquity(selected.key), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ points: equityPoints }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setMessage(
        `Saved “${selected.label || selected.key}” (${data.pointCount} points).`
        + (data.regeneratedMasterAccountGrid ? ' Master Multi Account weekday grid regenerated.' : ''),
      );
      // Soft: reload in place, so the table stays on the page you were editing
      // and shows what the server now has for that day.
      await loadScenarioData(selected, { soft: true });
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const downloadEquityCsv = async () => {
    if (!selected || selected.type !== 'equity' || equityPoints.length < 1) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioEquityExportCsv(selected.key), {
        method: 'POST',
        headers: authHeaders(),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data.error || 'Export failed');
      }
      const csv = await res.text();
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      const slug = selected.key.replace(/[^a-z0-9]+/gi, '_').replace(/^_|_$/g, '').toLowerCase();
      const filename = parseAttachmentFilename(
        res.headers.get('Content-Disposition'),
        `${slug}_equity_${stamp}.csv`,
      );
      downloadTextAsFile(csv, filename);
      setMessage(`Downloaded equity CSV (${equityPoints.length} points).`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const importEquityCsv = async (csvText, filename = '') => {
    if (!selected || selected.type !== 'equity' || !csvText.trim()) return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioEquityImport(selected.key), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ csv: csvText, filename }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Import failed');
      setMessage(
        `Imported ${data.pointCount} daily equity points from CSV`
        + (data.equityColumnUsed ? ` (column “${data.equityColumnUsed}”)` : '')
        + `.`
        + (data.liveStart
          ? ` The file marks live trading from ${data.liveStart}; backtested and live are joined without a restart. `
            + 'To show them separately, turn on “Backtested and live results” for this strategy on the Strategies page.'
          : '')
        + (data.regeneratedMasterAccountGrid ? ' Master Multi Account weekday grid regenerated.' : ''),
      );
      setCatalog((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          equityStrategies: (prev.equityStrategies || []).map((o) => (
            o.key === selected.key
              ? {
                ...o,
                pointCount: data.pointCount,
                startDate: data.strategy?.start_date || o.startDate,
                endDate: data.strategy?.end_date || o.endDate,
              }
              : o
          )),
        };
      });
      await loadScenarioData(selected);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveFutures = async () => {
    if (!selected || selected.type !== 'futures') return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioFutures(selected.key), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ rawData: futuresGrid }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setMessage(`Saved “${selected.label || selected.key}” futures grid.`);
      await loadScenarioData(selected);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const savePortfolio = async (payload) => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminEquityPortfolioConfig, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setPortfolioConfigs(data.configs || {});
      setMessage(`Saved ${PORTFOLIO_LABELS[payload.portfolioKey] || payload.portfolioKey}.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveNtConnectorTrades = async () => {
    if (!selected || selected.type !== 'nt-connector') return;
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioNtConnector(selected.key), {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ trades: ntConnectorTrades }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setNtConnectorMeta(data);
      setNtConnectorTrades(data.trades || []);
      setMessage(
        `Saved ${data.tradeCount} trades for ${data.accountName}. `
        + 'Manually edited rows are protected from the next connector sync.',
      );
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const rematchNtConnector = async () => {
    if (!selected || selected.type !== 'nt-connector') return;
    if (!window.confirm('Revert to connector-matched trades? This discards manual edits for this account and rebuilds trades from synced executions.')) {
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminScenarioNtConnectorRematch(selected.key), {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Rematch failed');
      setNtConnectorMeta(data);
      setNtConnectorTrades(data.trades || []);
      setMessage(`Reverted ${data.accountName} to ${data.tradeCount} connector-matched trades.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const saveMasterStrat = async (sleeves) => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(API_ENDPOINTS.adminMasterStrat, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ sleeves }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.error || 'Save failed');
      setMasterStrat((prev) => ({ ...prev, sleeves: data.sleeves }));
      setMessage(`Saved ${getEntityDisplayName()} Master Strat composition.`);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-feedback-page admin-sheet-manager">
      <div className="admin-sheet-header">
        <button type="button" className="admin-sheet-back" onClick={() => onNavigate && onNavigate('brand-strategies')}>
          ← Back
        </button>
        <h1 className="admin-sheet-title">Strategy Data Editor</h1>
      </div>

      {isVectorEntity() ? (
        <p className="admin-sheet-intro">
          Choose a {getEntityDisplayName()} strategy to inspect and edit its underlying data.
          <strong> Master Multi Account</strong>, <strong>all futures strategies</strong>, and
          <strong> 1H equity algos</strong> (AAPL, NVDA, etc.) edit individual trades
          from TradesViz / TradingView CSV exports — not raw equity points or weekday grids.
          Long-equity curves use daily <strong>date + equity</strong> CSV imports
          or point-by-point edits below. Saves rebuild simulator curves and, for futures, refresh the
          Results weekday grid automatically.
        </p>
      ) : (
        <p className="admin-sheet-intro">
          Pick a strategy to see and edit its data. Futures and stock strategies hold one row per trade (import or
          add rows below); daily-balance strategies hold one balance per day. Saving rebuilds the strategy’s curve.
        </p>
      )}

      {error ? <div className="admin-sheet-error" role="alert">{error}</div> : null}
      {message ? <div className="admin-sheet-message" role="status">{message}</div> : null}

      <div className="admin-scenario-toolbar">
        <label>
          <span>Strategy</span>
          <select
            value={selectedKey}
            onChange={(e) => { if (okToDropUnsaved()) setSelectedKey(e.target.value); }}
            disabled={loading || !options.length}
          >
            {!options.length ? <option value="">No strategies available</option> : null}
            {catalog?.configs?.length ? (
              <optgroup label="Configuration">
                {catalog.configs.map((o) => (
                  <option key={`${o.type}:${o.key}`} value={`${o.type}:${o.key}`}>
                    {formatScenarioDataOptionLabel(o)}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {catalog?.equityStrategies?.length ? (
              <optgroup label="Equity & trade journals">
                {catalog.equityStrategies.map((o) => (
                  <option key={`${o.type}:${o.key}`} value={`${o.type}:${o.key}`}>
                    {formatScenarioDataOptionLabel(o)}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {catalog?.futuresStrategies?.length ? (
              <optgroup label="Futures strategies (TradesViz)">
                {catalog.futuresStrategies.map((o) => (
                  <option key={`${o.type}:${o.key}`} value={`${o.type}:${o.key}`}>
                    {formatScenarioDataOptionLabel(o)}
                  </option>
                ))}
              </optgroup>
            ) : null}
            {isAdmin && catalog?.ntConnectorAccounts?.length ? (
              <optgroup label="Live NT accounts (connector v2)">
                {catalog.ntConnectorAccounts.map((o) => (
                  <option key={`${o.type}:${o.key}`} value={`${o.type}:${o.key}`}>
                    {o.label} ({o.tradeCount || 0} trades)
                  </option>
                ))}
              </optgroup>
            ) : null}
          </select>
        </label>
        <button type="button" className="admin-sheet-btn" onClick={() => loadCatalog({ quiet: true })} disabled={loading}>
          Refresh list
        </button>
      </div>

      {!loading && selected?.strategyRecord?.slug ? (
        <div className="admin-scenario-name-panel">
          <span className="admin-scenario-name-label">Name</span>
          <StrategyNameEditor
            slug={selected.strategyRecord.slug}
            name={selected.strategyRecord.displayName}
            onRenamed={() => loadCatalog({ quiet: true })}
          />
        </div>
      ) : null}

      {!loading && selected && INSTRUMENT_EDITOR_TYPES.has(selected.type) ? (
        <div className="admin-scenario-instrument-panel">
          <h2 className="admin-scenario-instrument-title">Instrument</h2>
          <p className="admin-scenario-import-hint">
            Choose the asset class and symbol shown on <strong>{getResultsNavLabel()}</strong> and the Portfolio Simulator
            (List of Trades). Futures strategies keep the NQ/MNQ contract toggle; stocks, ETFs, and crypto show dollar P&amp;L only.
          </p>
          <div className="admin-scenario-instrument-fields">
            <fieldset className="admin-scenario-instrument-class">
              <legend>Asset class</legend>
              {INSTRUMENT_CLASSES.map(({ value, label }) => (
                <label key={value} className="admin-scenario-instrument-class-option">
                  <input
                    type="radio"
                    name="instrumentClass"
                    value={value}
                    checked={instrumentClass === value}
                    onChange={() => setInstrumentClass(value)}
                  />
                  {label}
                </label>
              ))}
            </fieldset>
            <label className="admin-scenario-instrument-symbol">
              <span>Symbol</span>
              <input
                type="text"
                value={displaySymbol}
                onChange={(e) => setDisplaySymbol(e.target.value.toUpperCase())}
                placeholder={instrumentClass === 'futures' ? 'NQ' : instrumentClass === 'crypto' ? 'BTC' : 'AAPL'}
                spellCheck={false}
                autoComplete="off"
              />
            </label>
            <button
              type="button"
              className="admin-sheet-btn admin-sheet-btn-primary"
              onClick={saveInstrument}
              disabled={instrumentSaving || dataLoading || !displaySymbol.trim()}
            >
              {instrumentSaving ? 'Saving…' : 'Save instrument'}
            </button>
          </div>
          {instrumentInferred ? (
            <p className="admin-scenario-import-hint">
              Set automatically from the strategy’s name (a Vector Capital stock algo or ETF curve). Change it and Save
              instrument to override.
            </p>
          ) : null}
        </div>
      ) : null}

      {loading || dataLoading ? (
        <div className="admin-sheet-loading">Loading strategy data…</div>
      ) : null}

      {!loading && !dataLoading && selected?.type === 'nt-connector' && ntConnectorMeta && (
        <>
          <div className="admin-scenario-meta">
            <span><strong>Account:</strong> {ntConnectorMeta.accountName}</span>
            <span><strong>Trades:</strong> {ntConnectorTrades.length}</span>
            <span><strong>Manually edited:</strong> {ntConnectorMeta.manualCount || 0}</span>
            <span><strong>Base:</strong> ${(ntConnectorMeta.baseCapital || DEFAULT_BASE).toLocaleString()}</span>
            <span><strong>Last push:</strong> {ntConnectorMeta.lastSyncAt ? new Date(ntConnectorMeta.lastSyncAt).toLocaleString() : '—'}</span>
            {ntConnectorMeta.equityPreview?.startDate && (
              <span>
                <strong>Derived curve:</strong>
                {' '}
                {ntConnectorMeta.equityPreview.startDate}
                {' → '}
                {ntConnectorMeta.equityPreview.endDate}
                {' '}
                (${Number(ntConnectorMeta.equityPreview.endingEquity || 0).toLocaleString()})
              </span>
            )}
          </div>
          <div className="admin-scenario-import-hint" role="note">
            <strong>Live connector data:</strong> these trades come from your risklo-connector-v2 sync and power the{' '}
            <strong>NT8</strong> scenario on the Performance Dashboard. Edits save back to the live account; rows you
            change are flagged so the next NinjaTrader push won&apos;t overwrite them. Use <strong>Revert to connector
            match</strong> to rebuild from synced executions.
          </div>
          <div className="admin-scenario-actions">
            <button
              type="button"
              className="admin-sheet-btn"
              onClick={rematchNtConnector}
              disabled={saving}
            >
              Revert to connector match
            </button>
          </div>
          <TradeJournalEditor
            trades={ntConnectorTrades}
            onChange={setNtConnectorTrades}
            baseCapital={ntConnectorMeta.baseCapital || DEFAULT_BASE}
          />
          <div className="admin-scenario-actions">
            <button
              type="button"
              className="admin-sheet-btn admin-sheet-btn-primary"
              onClick={saveNtConnectorTrades}
              disabled={saving}
            >
              {saving ? 'Saving…' : 'Save trades to live account'}
            </button>
          </div>
        </>
      )}

      {/* Only reachable when no equity-strategy row owns this webhook's curve —
          the dropdown hides stubs that do (see shadowedWebhookSlugs). Without
          that row there is nothing point-by-point to edit, so say where the
          data went instead of implying the editor is simply empty. */}
      {!loading && !dataLoading && selected?.type === 'webhook' && (
        <div className="admin-scenario-import-hint" role="note">
          <strong>Live webhook strategy — no editable curve yet.</strong>{' '}
          Posts are being stored, but this scenario has no equity-strategy row, which is what the
          point-by-point editor reads. That happens when the webhook is bound to a Kairos
          <em> symbol</em> rather than to a strategy by name: the posts land in the shared Kairos
          daily-equity table instead of on a curve of its own.
          <br />
          <br />
          To get the editable table here, bind this scenario to a strategy name in{' '}
          <strong>Settings → Create Strategy</strong> (or Scenario Settings), then re-post or
          re-import. Incoming posts are visible now under <strong>Admin → Webhooks</strong> →
          Kairos daily equity.
        </div>
      )}

      {!loading && !dataLoading && selected?.type === 'trade-journal' && tradeJournalMeta && (
        <>
          <div className="admin-scenario-meta">
            <span><strong>Trades:</strong> {journalTrades.length}</span>
            <span><strong>Base:</strong> ${(tradeJournalMeta.baseCapital || DEFAULT_BASE).toLocaleString()}</span>
            {tradeJournalMeta.equityPreview?.startDate && (
              <span>
                <strong>Derived curve:</strong>
                {' '}
                {tradeJournalMeta.equityPreview.startDate}
                {' → '}
                {tradeJournalMeta.equityPreview.endDate}
                {' '}
                (${Number(tradeJournalMeta.equityPreview.endingEquity || 0).toLocaleString()})
              </span>
            )}
            {/* Which file produced the trades on screen. Absent only for
                strategies whose data never came from an upload (webhook-fed,
                sheet-seeded), or for imports done before this was tracked. */}
            {tradeJournalMeta.lastUpload?.filename ? (
              <span
                className="admin-scenario-meta-file"
                title={[
                  `Imported ${tradeJournalMeta.lastUpload.tradeCount ?? '—'} trades`,
                  tradeJournalMeta.lastUpload.sourceFormat
                    ? `format: ${tradeJournalMeta.lastUpload.sourceFormat}`
                    : null,
                  tradeJournalMeta.lastUpload.uploadedBy
                    ? `by ${tradeJournalMeta.lastUpload.uploadedBy}`
                    : null,
                ].filter(Boolean).join(' · ')}
              >
                <strong>Last CSV:</strong>
                {' '}
                <code>{tradeJournalMeta.lastUpload.filename}</code>
                {tradeJournalMeta.lastUpload.uploadedAt
                  ? ` · ${new Date(tradeJournalMeta.lastUpload.uploadedAt).toLocaleString()}`
                  : ''}
              </span>
            ) : null}
          </div>
          <div className="admin-scenario-import-hint" role="note">
            {tradeJournalMeta.editorVariant === 'vector-equity' ? (
              <>
                <strong>VECTOR / TradingView trade list:</strong> upload the same
                {' '}
                <code>VECTOR_[TICKER]_…Trades.csv</code>
                {' '}
                exported from TradingView, or click <strong>Reload from ingested CSV</strong> to pull the matching file from
                {' '}
                <code>core_csv/</code>
                . Edit the same fields as the List of Trades — exit date, symbol, volume,
                entry/exit prices, and Net P&L ($ or %). The sim curve rebuilds from the % column when you save.
              </>
            ) : (
              <>
                <strong>Trade imports:</strong> TradesViz <strong>Trades</strong> export or a NinjaTrader
                executions export.
                <ul>
                  <li>
                    <strong>TradesViz Trades export</strong> — one row per completed trade with net P&L
                    from TradesViz. This matches <code>Masteraccount_combined_tradesviz.csv</code>.
                    {' '}
                    <strong>Import TradesViz Trades export</strong> replaces all current trades (you will be
                    asked to confirm). To append instead, use <strong>Add partial data</strong>.
                  </li>
                  <li>
                    <strong>NinjaTrader executions export</strong> (Instrument / Action / Quantity / Price /
                    Time / E-X) — RiskLo pairs the fills into completed trades and computes P&L from prices
                    × the contract&apos;s $/point (NQ, MNQ, ES, MES, YM, RTY, GC, CL and micros), minus commissions.
                  </li>
                  <li>
                    <strong>TradesViz Executions export</strong> — fills only; P&L is estimated. Use one of the
                    formats above when available.
                  </li>
                  <li>
                    <strong>Add partial data</strong> — append a TradesViz or NinjaTrader CSV to the trades
                    already in the table without replacing them. Review, then save &amp; rebuild the curve.
                  </li>
                  <li>
                    <strong>Live / backtest</strong> — any of these files can add a <code>segment</code> column
                    (<code>live</code> or <code>backtest</code>); the first live trade is where live trading
                    started. Without one, the Live / Backtest column stays blank.
                  </li>
                </ul>
              </>
            )}
          </div>
          <div className="admin-scenario-actions">
            <button
              type="button"
              className="admin-sheet-btn"
              onClick={reloadFromIngestedCsv}
              disabled={saving}
            >
              Reload from ingested CSV
            </button>
            <button
              type="button"
              className="admin-sheet-btn"
              onClick={downloadNormalizedCsv}
              disabled={saving || journalTrades.length < 1}
            >
              Download normalized CSV
            </button>
            <label className="admin-equity-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
              <span>
                {tradeJournalMeta.editorVariant === 'vector-equity'
                  ? 'Import VECTOR / TradingView Trades CSV'
                  : 'Import TradesViz Trades export'}
              </span>
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={saving}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  if (!confirmFullTradeImport(file.name)) {
                    e.target.value = '';
                    return;
                  }
                  const reader = new FileReader();
                  // Pass the filename through so the editor can show which file
                  // the displayed trades came from, long after this upload.
                  reader.onload = () => importTradeCsv(String(reader.result || ''), file.name);
                  reader.readAsText(file);
                  e.target.value = '';
                }}
              />
            </label>
            <PartialDataFileInput disabled={saving} onSelect={onPartialDataFileSelect} />
          </div>
          <TradeJournalEditor
            trades={journalTrades}
            onChange={setJournalTrades}
            baseCapital={tradeJournalMeta.baseCapital || DEFAULT_BASE}
            variant={tradeJournalMeta.editorVariant || 'futures'}
            defaultSymbol={
              tradeJournalMeta.editorVariant === 'vector-equity'
                ? tickerFromVectorStrategyName(selected?.key)
                : ''
            }
            onAddRejected={(msg) => {
              setError(msg);
              if (msg) setMessage(null);
            }}
          />
          <div className="admin-scenario-actions">
            <PartialDataFileInput disabled={saving} onSelect={onPartialDataFileSelect} />
          </div>
          <div className="admin-scenario-actions">
            {journalDirty && !saving ? (
              <span className="admin-scenario-unsaved" role="status">
                Unsaved changes: they are only in this browser until you save.
              </span>
            ) : null}
            <button
              type="button"
              className="admin-sheet-btn admin-sheet-btn-primary"
              onClick={saveTrades}
              disabled={saving || journalTrades.length < 1}
            >
              {saving ? 'Saving… (this can take up to a minute)' : 'Save trades & rebuild curve'}
            </button>
          </div>
        </>
      )}

      {!loading && !dataLoading && selected?.type === 'equity' && equityMeta && (
        <>
          <div className="admin-scenario-meta">
            <span><strong>Source:</strong> {sourceLabel(equityMeta.source)}</span>
            <span><strong>Range:</strong> {(equityMeta.startDate || '').slice(0, 10)} → {(equityMeta.endDate || '').slice(0, 10)}</span>
            <span><strong>Points:</strong> {equityPoints.length}</span>
            {equityMeta.lastCsvFilename ? (
              <span>
                <strong>Last CSV:</strong> {equityMeta.lastCsvFilename}
                {equityMeta.lastCsvUploadedAt ? ` · ${new Date(equityMeta.lastCsvUploadedAt).toLocaleDateString()}` : ''}
              </span>
            ) : null}
            {equityLiveOverlay?.active ? (
              <span>
                <strong>{equityLiveOverlay.mirrored ? 'Mirrored live overlay' : 'NT8 live overlay'}:</strong>{' '}
                {equityLiveOverlay.mirrorLiveLabel || equityLiveOverlay.overlayLabel || 'configured'}
                {equityLiveOverlay.webhookPointCount
                  ? ` · ${equityLiveOverlay.webhookPointCount} webhook row(s)`
                  : ''}
              </span>
            ) : null}
          </div>
          {selected?.webhook ? (
            <WebhookTokenPanel
              webhook={selected.webhook}
              disabled={saving}
              onMessage={setMessage}
              onError={setError}
            />
          ) : null}
          <div className="admin-scenario-import-hint" role="note">
            <strong>Daily equity CSV:</strong> upload a file with a <code>date</code> column (YYYY-MM-DD)
            and an equity/value column (e.g. <code>date,equity</code>). Optional: a <code>segment</code> column
            (<code>live</code> / <code>backtest</code>) marks when live trading started, and the live part is joined
            without a restart. Import replaces the full curve
            for <strong>{selected.label || selected.key}</strong>. You can still tweak individual days in the table below
            and click <strong>Save equity curve</strong>.
          </div>
          <div className="admin-scenario-actions">
            <button
              type="button"
              className="admin-sheet-btn"
              onClick={downloadEquityCsv}
              disabled={saving || equityPoints.length < 1}
            >
              Download equity CSV
            </button>
            <label className="admin-equity-field" style={{ flexDirection: 'row', alignItems: 'center', gap: '0.5rem' }}>
              <span>Import daily equity CSV</span>
              <input
                type="file"
                accept=".csv,text/csv"
                disabled={saving}
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => importEquityCsv(String(reader.result || ''), file.name);
                  reader.onerror = () => setError('Could not read the file.');
                  reader.readAsText(file);
                  e.target.value = '';
                }}
              />
            </label>
          </div>
          <EquityPointsEditor
            points={equityPoints}
            onChange={setEquityPoints}
            defaultSource={equityMeta.source}
          />
          <div className="admin-scenario-actions">
            <button
              type="button"
              className="admin-sheet-btn admin-sheet-btn-primary"
              onClick={saveEquity}
              disabled={saving || equityPoints.length < 2}
            >
              {saving ? 'Saving…' : 'Save equity curve'}
            </button>
          </div>
        </>
      )}

      {!loading && !dataLoading && selected?.type === 'futures' && futuresMeta && (
        <>
          <div className="admin-scenario-meta">
            <span><strong>Source sheet:</strong> {futuresMeta.sourceSheetName}</span>
            <span><strong>Rows:</strong> {futuresGrid.length}</span>
            <span><strong>Last synced:</strong> {futuresMeta.lastSyncedAt ? new Date(futuresMeta.lastSyncedAt).toLocaleString() : '—'}</span>
          </div>
          <FuturesGridEditor rawData={futuresGrid} onChange={setFuturesGrid} />
          <div className="admin-scenario-actions">
            <button
              type="button"
              className="admin-sheet-btn admin-sheet-btn-primary"
              onClick={saveFutures}
              disabled={saving || futuresGrid.length < 3}
            >
              {saving ? 'Saving…' : 'Save futures grid'}
            </button>
          </div>
        </>
      )}

      {!loading && !dataLoading && selected?.type === 'portfolio' && portfolioConfigs && (
        <PortfolioConfigEditor
          portfolioKey={selected.key}
          config={portfolioConfigs[selected.key]}
          onSave={savePortfolio}
        />
      )}

      {!loading && !dataLoading && selected?.type === 'master-strat' && masterStrat && (
        <MasterStratEditor
          sleeves={masterStrat.sleeves}
          strategyOptions={masterStrat.strategyOptions}
          onSave={saveMasterStrat}
        />
      )}
    </div>
  );
}

// For tests only.
export const _scenarioDataInternals = {
  planJournalSave, mergePartialTrades, liveConflictDays, missingAfterSave, EquityPointsEditor, TradeJournalEditor,
};
